import {
  ADD_PROPOSED_ACTIONS,
  FETCHING_PROPOSED_ACTIONS,
  REVIEW_PROPOSED_ACTION,
} from '../constants';

export interface ProposedAction {
  id: number;
  action_type: string;
  source_agent: string;
  source_entity_type: string | null;
  source_entity_id: number | null;
  rationale: string;
  payload: Record<string, any>;
  confidence: number;
  status: 'pending' | 'approved' | 'rejected' | 'auto_executed' | 'expired';
  expires_at: string;
  created_at: string;
  /**
   * The plan_task_definitions row this action satisfies, if any. Tagged
   * server-side at insert time via
   * backend/api/client/utilities/plan-task-action-link.js. When an action
   * with a related_task_id is approved, the linked PlanTask
   * auto-completes via project_task_completions. PR H renders the action
   * inline under its parent task on PlanTasksScreen.
   */
  related_task_id?: number | null;
}

/**
 * The record an approval made, reported to the server with the approval so that
 * "what did this suggestion become?" has an answer (the AI assistant connector's
 * "delete what it added" needs it). The server checks it: a kind the proposal
 * creates, in the proposal's plan, made by the reviewer, and still live.
 *
 *   medication      the new medication's id
 *   contact         the new contact's id
 *   calendar_event  the local event's id ("loc_…")
 *   reminder        config.config.reminder_id
 */
export interface CreatedRecord {
  type: 'medication' | 'contact' | 'calendar_event' | 'reminder';
  id: string | number;
}

export interface DelegationPrompt {
  action_type: string;
  approvals_so_far: number;
  threshold: number;
  delegation_prompt: string;
  prompt?: string;
}

export interface ExistingGrant {
  action_type: string;
  granted_at: string;
  decision_criteria: string | null;
}

interface ProposedActionsState {
  list: Record<string, ProposedAction[]>;
  delegationPrompts: Record<string, DelegationPrompt[]>;
  existingGrants: Record<string, ExistingGrant[]>;
  fetching: boolean;
}

const initialState: ProposedActionsState = {
  list: {},
  delegationPrompts: {},
  existingGrants: {},
  fetching: false,
};

export default function proposedActionsReducer(
  state = initialState,
  { type, payload }: { type: string; payload?: any },
): ProposedActionsState {
  switch (type) {
    case ADD_PROPOSED_ACTIONS:
      return {
        ...state,
        list: { ...state.list, [payload.project_id]: payload.list },
        delegationPrompts: { ...state.delegationPrompts, [payload.project_id]: payload.delegationPrompts },
        existingGrants: { ...state.existingGrants, [payload.project_id]: payload.existingGrants },
      };
    case FETCHING_PROPOSED_ACTIONS:
      return { ...state, fetching: payload };
    case REVIEW_PROPOSED_ACTION: {
      const { project_id, actionId, decision } = payload;
      const existing = state.list[project_id] ?? [];
      return {
        ...state,
        list: {
          ...state.list,
          [project_id]: existing.map((a) =>
            a.id === actionId ? { ...a, status: decision } : a,
          ),
        },
      };
    }
    default:
      return state;
  }
}
